// src/pages/Customers.js - Updated with Export + Bulk Actions
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ExportButton from '../components/ExportButton'; // Import
import BulkActions from '../components/BulkActions'; // Import
import DateRangeFilter from '../components/DateRangeFilter'; // Import

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockUserId, setBlockUserId] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [approving, setApproving] = useState(false);
  
  // Bulk actions state
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  
  // Date range state
  const [dateRange, setDateRange] = useState(null);
  
  const navigate = useNavigate();

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [searchTerm, customers, dateRange]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/all-riders`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setCustomers(response.data.riders || []);
      setFilteredCustomers(response.data.riders || []);
      setSelectedCustomers([]);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = [...customers];
    
    // Apply date filter
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      end.setHours(23, 59, 59);
      filtered = filtered.filter(c => {
        const date = new Date(c.created_at);
        return date >= start && date <= end;
      });
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(customer => {
        const name = (customer.full_name || '').toLowerCase();
        const phone = (customer.phone_number || '').toLowerCase();
        const email = (customer.email || '').toLowerCase();
        const id = String(customer.id || '');
        const status = customer.is_blocked ? 'blocked' : 'active';
        
        return name.includes(term) || 
               phone.includes(term) || 
               email.includes(term) || 
               id.includes(term) ||
               status.includes(term);
      });
    }
    
    setFilteredCustomers(filtered);
  };

  const goToCustomerDetails = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const getCustomerStatus = (customer) => {
    if (customer.is_blocked) {
      return { status: 'blocked', label: '🚫 Blocked', color: '#ef4444', bg: '#fee2e2' };
    }
    return { status: 'active', label: '✅ Active', color: '#10b981', bg: '#dcfce7' };
  };

  const blockCustomer = async () => {
    if (!blockReason.trim()) {
      alert('Please enter a reason for blocking');
      return;
    }
    
    setApproving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/customer/block/${blockUserId}`,
        { reason: blockReason },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('Customer blocked successfully');
      setShowBlockModal(false);
      setBlockReason('');
      setBlockUserId(null);
      fetchCustomers();
    } catch (error) {
      alert('Failed to block customer: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const unblockCustomer = async (userId) => {
    if (!window.confirm('Unblock this customer?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/customer/unblock/${userId}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('Customer unblocked successfully');
      fetchCustomers();
    } catch (error) {
      alert('Failed to unblock customer: ' + (error.response?.data?.error || error.message));
    }
  };

  // Export data
  const getExportData = () => {
    return filteredCustomers.map(c => ({
      ID: c.id,
      Name: c.full_name || '—',
      Phone: c.phone_number,
      Email: c.email || '—',
      'Total Rides': c.total_rides || 0,
      Joined: new Date(c.created_at).toLocaleDateString(),
      Status: c.is_blocked ? 'Blocked' : 'Active',
    }));
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCustomers(filteredCustomers.map(c => c.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  // Handle individual select
  const handleSelectCustomer = (customerId, checked) => {
    if (checked) {
      setSelectedCustomers([...selectedCustomers, customerId]);
    } else {
      setSelectedCustomers(selectedCustomers.filter(id => id !== customerId));
    }
  };

  const searchStats = {
    total: customers.length,
    shown: filteredCustomers.length,
    hidden: customers.length - filteredCustomers.length
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading customers...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Customers</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#64748b', flexWrap: 'wrap' }}>
            <span>👥 {customers.length} total</span>
            <span style={{ color: '#ef4444' }}>🚫 {customers.filter(c => c.is_blocked).length} blocked</span>
            {searchTerm && (
              <span style={{ color: '#f97316' }}>
                🔍 {filteredCustomers.length} shown
                {searchStats.hidden > 0 && ` (${searchStats.hidden} hidden)`}
              </span>
            )}
            {selectedCustomers.length > 0 && (
              <span style={{ color: '#3b82f6' }}>
                ☑️ {selectedCustomers.length} selected
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Date Range Filter */}
          <DateRangeFilter 
            onApply={(range) => setDateRange(range)}
            onClear={() => setDateRange(null)}
            label="Filter by Date"
          />
          
          {/* Export Button */}
          <ExportButton 
            data={getExportData()} 
            filename="customers_export" 
            label="Export CSV"
          />
          
          {/* Bulk Actions */}
          <BulkActions 
            selectedIds={selectedCustomers}
            onActionComplete={() => {
              setSelectedCustomers([]);
              fetchCustomers();
            }}
            type="customers"
            API_URL={API_URL}
          />
          
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '10px 16px',
                paddingRight: searchTerm ? '40px' : '16px',
                borderRadius: '10px',
                border: '2px solid #e2e8f0',
                width: '280px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#94a3b8',
                }}
              >
                ✕
              </button>
            )}
          </div>
          
          <button
            onClick={fetchCustomers}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '14px 18px',
          borderRadius: '10px',
          marginBottom: '16px',
          border: '1px solid #fecaca',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Stats Cards */}
      {searchTerm && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '12px 16px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Total</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{searchStats.total}</div>
          </div>
          <div style={{
            backgroundColor: 'white',
            padding: '12px 16px',
            borderRadius: '10px',
            border: '1px solid #10b981',
          }}>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Showing</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#10b981' }}>{searchStats.shown}</div>
          </div>
          {searchStats.hidden > 0 && (
            <div style={{
              backgroundColor: 'white',
              padding: '12px 16px',
              borderRadius: '10px',
              border: '1px solid #f59e0b',
            }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Hidden</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f59e0b' }}>{searchStats.hidden}</div>
            </div>
          )}
        </div>
      )}

      {/* Customers Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'auto',
      }}>
        {filteredCustomers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <p style={{ color: '#64748b', fontSize: '16px' }}>
              {searchTerm ? `No customers found matching "${searchTerm}"` : 'No customers yet'}
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginTop: '8px',
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>
                  <input
                    type="checkbox"
                    checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>ID</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Phone</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Total Rides</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Joined</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => {
                const status = getCustomerStatus(customer);
                return (
                  <tr key={customer.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={(e) => handleSelectCustomer(customer.id, e.target.checked)}
                      />
                    </td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>#{customer.id}</td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {customer.full_name || '—'}
                    </td>
                    <td style={{ padding: '12px' }}>{customer.phone_number}</td>
                    <td style={{ padding: '12px' }}>{customer.email || '—'}</td>
                    <td style={{ padding: '12px' }}>{customer.total_rides || 0}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        backgroundColor: status.bg,
                        color: status.color,
                        fontWeight: '500',
                      }}>
                        {status.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#64748b' }}>
                      {new Date(customer.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <button
                        onClick={() => goToCustomerDetails(customer.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginRight: '6px',
                        }}
                      >
                        👁️ View
                      </button>
                      {customer.is_blocked ? (
                        <button
                          onClick={() => unblockCustomer(customer.id)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          🔄 Unblock
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setBlockUserId(customer.id);
                            setShowBlockModal(true);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          🚫 Block
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        
        {filteredCustomers.length > 0 && (
          <div style={{ 
            padding: '12px', 
            color: '#64748b', 
            fontSize: '13px',
            borderTop: '1px solid #e2e8f0',
            marginTop: '12px',
          }}>
            Showing {filteredCustomers.length} of {customers.length} customers
          </div>
        )}
      </div>

      {/* Block Modal */}
      {showBlockModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#ef4444' }}>🚫 Block Customer</h3>
            <p>This customer will be blocked from booking rides.</p>
            <textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Reason for blocking (required)"
              rows="3"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginBottom: '20px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowBlockModal(false); setBlockReason(''); setBlockUserId(null); }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={blockCustomer}
                disabled={approving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: approving ? 'not-allowed' : 'pointer',
                  opacity: approving ? 0.6 : 1
                }}
              >
                Block Customer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Customers;